<script lang="ts">
  // components
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import PaginationLarge from "@shared/svelte/Components/Pagination/Large.svelte";

  // types
  import type { PaginationDetails } from "@shared/types/Pagination";

  // props
  export let pagination: PaginationDetails;
  export let openDeleteModal: (credentialId: number, distributorName: string) => void;

  // stores
  import { CredentialManagementStore } from "@retailer/stores/CredentialManagementStore";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";

  // services
  import * as CredentialManagementService from "@retailer/services/credential_management_service";

  $: pageNum = pagination.page;

  // data
  const PER_PAGE = 10;

  const sortByOptions = {
    dist_a_to_z: {
      order: "distributors.name",
      label: "Distributor A-Z",
      direction: "ASC",
    },
    dist_z_to_a: {
      order: "distributors.name",
      label: "Distributor Z-A",
      direction: "DESC",
    },
    hostname: {
      order: "server_hostname",
      label: "Hostname",
      direction: "ASC",
    },
    transfer_method: {
      order: "file_transfer_type",
      label: "File Transfer Method",
      direction: "ASC",
    },
  };

  // functions
  const filter = async () => {
    const { order, direction } = sortByOptions[selectedOption];
    const filters = {
      search,
      order,
      direction,
    };

    await CredentialManagementService.getCredentials(1, filters);
  };

  let direction = $CredentialManagementStore.filters.direction;
  let order = $CredentialManagementStore.filters.order;
  let search = $CredentialManagementStore.filters.search;

  let selectedOption = Object.keys(sortByOptions).find(
    key => sortByOptions[key].order === order && sortByOptions[key].direction === direction,
  );

  const fileTransferMethodText = (fileTransferMethod: string) => {
    if (fileTransferMethod === "sftp") return "SSH (sftp/scp)";
    if (fileTransferMethod === "as2") return "As2";
  };
</script>

<div
  class="fizz-container fizz-inset-square-24"
  class:fizz-flex-row={$LayoutStore.isDesktop}
  class:fizz-flex-vertical={$LayoutStore.isMobile}
>
  <p
    class="fizz-text-subdued fizz-flex-item"
    id="pagination-placement"
    class:fizz-stack-24={$LayoutStore.isMobile}
  >
    Showing {pagination.totalEntries == 0 ? 0 : (pageNum - 1) * PER_PAGE + 1}-{pageNum * PER_PAGE >
    pagination.totalEntries
      ? pagination.totalEntries
      : pageNum * PER_PAGE} of {pluralize("Distributor", pagination.totalEntries, true)}
  </p>
  <div
    class="fizz-flex-item"
    class:fizz-flex-row={$LayoutStore.isDesktop}
    class:fizz-justify-end={$LayoutStore.isDesktop}
    class:fizz-flex-vertical={$LayoutStore.isMobile}
  >
    <input
      type="search"
      id="distributor-input-search"
      data-testid="input-search"
      name="input-search"
      placeholder="Find a Distributor"
      class="fizz-input-search"
      class:fizz-full-width={$LayoutStore.isMobile}
      class:fizz-stack-16={$LayoutStore.isMobile}
      bind:value={search}
      on:change={filter}
    />
    <select
      class="fizz-select fizz-text-strong"
      class:fizz-full-width={$LayoutStore.isMobile}
      name="credential_sort_by"
      id="credential_sort_by"
      bind:value={selectedOption}
      on:change={filter}
    >
      {#each Object.entries(sortByOptions) as [key, value]}
        <option value={key}>
          {selectedOption === key ? "Sort by: " : ""}{value.label}
        </option>
      {/each}
    </select>
  </div>
</div>
<div class="overflow">
  <table
    class="fizz-table fizz-stack-24"
    style={$LayoutStore.isDesktop ? "--heading-col-width: 120px" : "--heading-col-width: 8px"}
  >
    <thead>
      <tr>
        <th>Distributor</th>
        <th>File Transfer Method</th>
        <th>Hostname</th>
        <th>Port</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      {#each $CredentialManagementStore.credentials as credential}
        <tr id="credential-{credential.id}" data-testid="credential-entry">
          <td
            data-label={$LayoutStore.isDesktop ? "Distributor" : ""}
            class="fizz-text-interactive fizz-text-strong"
          >
            {credential.distributor.name}
          </td>
          <td data-label={$LayoutStore.isDesktop ? "File Transfer Method" : ""}>
            {fileTransferMethodText(credential.file_transfer_type)}
          </td>
          <td data-label={$LayoutStore.isDesktop ? "Hostname" : ""}>{credential.server_hostname}</td
          >
          <td data-label={$LayoutStore.isDesktop ? "Port" : ""}>{credential.server_port}</td>
          <td width={$LayoutStore.isMobile ? "100%" : "12%"}>
            <button
              class="fizz-button-text"
              class:fizz-full-width={$LayoutStore.isMobile}
              on:click={() => openDeleteModal(credential.id, credential.distributor.name)}
              >Remove</button
            >
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
  <div class="fizz-stack-48">
    <PaginationLarge {pagination} />
  </div>
</div>
